import React, { useCallback, useEffect, useState } from "react";
import {
  BankOutlined,
  CarryOutOutlined,
  EditOutlined,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  CopyOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { Alert, Button, message, Spin, Tag, Tooltip } from "antd";
import GetMerchantData from "../../functions/merchant/getMerchantData";
import { getMerchantId } from "service/storage";
import { bg_button, bg_shadow, bg_text } from "constant/css";
import MerchantOrganizationEditModal from "../../components/modal/merchantOrganizationEditModal";
import MerchantChangePasswordModal from "../../components/modal/merchantChangePasswordModal";

const storageKey = () =>
  sessionStorage.getItem("isImpersonation") === "true"
    ? "sessionStorage"
    : "localStorage";

const persistMerchantData = (data) => {
  const json = JSON.stringify(data);
  if (storageKey() === "sessionStorage") {
    sessionStorage.setItem("merchantData", json);
  } else {
    localStorage.setItem("merchantData", json);
  }
};

const MerchantOrganization = () => {
  const merchantId = getMerchantId();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fetchInfo = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    const merchant = await GetMerchantData(merchantId);
    setData(merchant);
    if (merchant) persistMerchantData(merchant);
    setLoading(false);
  }, [merchantId]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success("Хуулсан: " + text);
  };

  const onSaved = (updated) => {
    setData(updated);
    persistMerchantData(updated);
  };

  if (loading || !data) {
    return (
      <Spin size="large" className="block mx-auto my-10 custom-green-spin" />
    );
  }

  return (
    <div className={`rounded-2xl p-6 bg-white text-gray-800 ${bg_shadow}`}>
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Банк, данс, багц, шимтгэл"
        description="Эдгээр тохиргоог зөвхөн админ өөрчилнө. Холбоо барих мэдээллээ доор засна."
      />
      <div className="flex justify-end gap-2 mb-4">
        <Button icon={<KeyOutlined />} onClick={() => setShowPassword(true)}>
          Нууц үг солих
        </Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          className={`${bg_button}`}
          onClick={() => setShowEdit(true)}
        >
          Засах
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 text-sm font-medium">
        <div className="sm:col-span-2 text-lg font-semibold text-gray-900">
          {data.name}
        </div>
        <div className="flex gap-2 items-center sm:col-span-2">
          Нэвтрэх нэр (портал / API):{" "}
          <span className="font-medium">
            {data.authUsername || data.email || "—"}
          </span>
          <Tooltip title="Хуулах">
            <CopyOutlined
              className="cursor-pointer"
              onClick={() =>
                copyToClipboard(data.authUsername || data.email)
              }
            />
          </Tooltip>
        </div>
        <div className="flex gap-2 items-center">
          <MailOutlined />
          Имэйл: {data.email || "—"}
          <Tooltip title="Хуулах">
            <CopyOutlined
              className="cursor-pointer"
              onClick={() => copyToClipboard(data.email)}
            />
          </Tooltip>
        </div>
        <div className="flex gap-2 items-center">
          Байгууллагын регистр: {data.register || "—"}
          <Tooltip title="Хуулах">
            <CopyOutlined
              className="cursor-pointer"
              onClick={() => copyToClipboard(data.register)}
            />
          </Tooltip>
        </div>
        <div className="flex gap-2 items-center">
          <PhoneOutlined />
          Утас 1: {data.phone || "—"}
        </div>
        <div className="flex gap-2 items-center">
          Утас 2: {data.phone2 || "—"}
        </div>
        <div className="flex gap-2 items-center sm:col-span-2">
          <HomeOutlined />
          Байршил: {data.address || "—"}
        </div>
        <div className="flex gap-2 items-center">
          <CarryOutOutlined />
          Багц: <Tag color="cyan">{data.pack || "—"}</Tag>
        </div>
        <div className="flex gap-2 items-center sm:col-span-2">
          <BankOutlined />
          Банк:
          <span className={bg_text}>
            {data.accountNumber || "—"} ({data.bankCode || "—"})
          </span>
        </div>
        <div className="sm:col-span-2 flex flex-wrap items-center gap-2 mt-2">
          <Tag color="cyan">Төлбөрийн шимтгэл: {data.feePercent}%</Tag>
          <Tag color="cyan">xPay шимтгэл: {data.spayFeePercent}%</Tag>
          <Tag color="cyan">
            Хэрэглэгч шимтгэл: {data.userFee}
            {data.userFeeType === "PERCENT" ? "%" : "₮"}
          </Tag>
        </div>
      </div>
      {showEdit && (
        <MerchantOrganizationEditModal
          open={showEdit}
          close={() => setShowEdit(false)}
          merchant={data}
          onSaved={onSaved}
        />
      )}
      <MerchantChangePasswordModal
        open={showPassword}
        close={() => setShowPassword(false)}
      />
    </div>
  );
};

export default MerchantOrganization;
